import { type GenericMap } from "@common/declarations";
import { sort } from "@common/lib/array";
import date from "@common/lib/date";
import { type I18n, i18nFormat } from "@common/lib/i18n";
import { Common } from "@common/types";
import { type FormSurvey } from "./types";

export const getNextPage = (
  survey: FormSurvey,
  currentPage: Common.System.FormPage,
  pageData: Common.GenericMap<Common.GenericMap<any>>
): Common.System.FormPage | null => {
  const groups = sort(
    survey.conditionGroups.filter((x) => x.formPageId === currentPage.formPageId),
    { key: "sortOrder" }
  )
    .map((x) => ({
      conjuction: x.conjunction,
      actions: survey.actions.filter(
        (y) => y.formPageId === x.formPageId && y.formPageConditionGroupId === x.formPageConditionGroupId
      ),
      conditions: survey.conditions.filter(
        (y) => y.formPageId === x.formPageId && y.formPageConditionGroupId === x.formPageConditionGroupId
      )
    }))
    // protection: remove groups that have no actions or conditions
    .filter((x) => x.actions.length > 0 && x.conditions.length > 0);

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];

    const doesMatch = group.conditions.reduce((res, x) => {
      const field = survey.fields.find((y) => y.formFieldId === x.formFieldId)!;
      const dm = doesMatchCondition(field, x, pageData[field.formPageId]);
      return group.conjuction === "AND" ? res && dm : res || dm;
    }, true);

    if (doesMatch) {
      const action = group.actions.find((x) => x.goToFormPageId);
      if (action) {
        return survey.pages.find((x) => x.formPageId === action.goToFormPageId)!;
      }
    }
  }

  const nextPage = survey.pages.find((x) => x.formPageId === currentPage.nextFormPageId);
  if (nextPage) {
    return nextPage;
  }

  const pages = sort(
    survey.pages.filter((x) => x.formPageTypeKey === "Standard"),
    { key: "sortOrder" }
  );
  const index = pages.findIndex((x) => x.formPageId === currentPage.formPageId);
  if (index < pages.length - 1) return pages[index + 1];

  // endings
  const endings = sort(
    survey.pages.filter((x) => x.formPageTypeKey === "Ending"),
    { key: "sortOrder" }
  );
  return endings[0];
};

export const doesMatchCondition = (
  field: Common.System.FormField,
  condition: Common.System.FormPageCondition,
  pageData: GenericMap<any> | undefined
): boolean => {
  if (!pageData || !(field.formFieldId in pageData)) return false;

  const value = pageData[field.formFieldId];
  switch (condition.conditionOperator) {
    case "Contains": {
      return (value.toString() as string).toLowerCase().includes(condition.value.toLowerCase());
    }
    case "DoesNotContain": {
      return !(value.toString() as string).toLowerCase().includes(condition.value.toLowerCase());
    }
    case "EndsWith": {
      return (value.toString() as string).toLowerCase().endsWith(condition.value.toLowerCase());
    }
    case "Is": {
      if (field.formControlKey === "Checkbox") {
        return Boolean(condition.value) === Boolean(value);
      }
      return condition.value == value;
    }
    case "IsAfter": {
      return new Date(value) > new Date(condition.value);
    }
    case "IsAfterOrOn": {
      return date.differenceInDays(new Date(value), new Date(condition.value)) >= 0;
    }
    case "IsBefore": {
      return new Date(value) < new Date(condition.value);
    }
    case "IsBeforeOrOn": {
      return date.differenceInDays(new Date(value), new Date(condition.value)) <= 0;
    }
    case "IsGreaterOrEqualTo": {
      return +value >= +condition.value;
    }
    case "IsGreaterThan": {
      return +value > +condition.value;
    }
    case "IsLowerOrEqualTo": {
      return +value <= +condition.value;
    }
    case "IsLowerThan": {
      return +value < +condition.value;
    }
    case "IsNot": {
      return condition.value != value;
    }
    case "IsNotOn": {
      return date.differenceInDays(new Date(value), new Date(condition.value)) !== 0;
    }
    case "IsOn": {
      return date.differenceInDays(new Date(value), new Date(condition.value)) === 0;
    }
    case "StartsWith": {
      return (value.toString() as string).toLowerCase().startsWith(condition.value.toLowerCase());
    }
    default:
      return false;
  }
};

export const getPageName = (i18n: I18n, name: string | null, index: number): string =>
  name || i18nFormat(i18n.pageN, ["N", index + 1]);
